// Shared rules engine for "closing" games (Cricket, Tactics, Cutthroat, ...):
// a fixed (or per-game-random) set of targets, each hit adds a mark (1-3) for
// the player who threw it, and once a player has 3 marks on a target,
// further hits are worth points - but only as long as at least one player
// hasn't closed that target too ("dead" targets do nothing for anyone).
//
// What differs between variants is only WHO those points go to - normal
// cricket credits the throwing player; Cutthroat instead credits every
// opponent who hasn't closed the target (you're "attacking" their score, and
// lowest score wins). That's the one pluggable piece, `awardScore`.
//
// State is intentionally just two numbers per (player, target): marks and
// score. "Is this target dead"/"is it still scorable" is recomputed from
// every player's current marks on every call rather than stored, so closing
// or re-opening a target for one player automatically reflects onto every
// other player's cells for free.
//
// A single global Undo needs to reverse whatever the most recent hit did,
// wherever on the board it landed and whoever it credited - since Cutthroat
// can credit players other than the one who threw the dart, undo can't just
// infer "was this a mark or a score" by looking at the thrower's own cell
// (that heuristic only works when hits always credit yourself). Instead each
// hit returns an explicit, self-describing effect - {type: 'mark', ...} or
// {type: 'score', credits: [{recipient, amount}, ...]} - and that's what
// goes on the history stack, so undo just replays it backwards exactly.

function targetValue(target) {
    return target === 'bull' ? 25 : Number(target);
}

export function createClosingGameState(players, targets) {
    const state = {};
    players.forEach(player => {
        state[player] = {};
        targets.forEach(target => {
            state[player][target] = { marks: 0, score: 0 };
        });
    });
    return state;
}

// True once every player has closed (3+ marks on) this target - marks are
// done changing for it at that point, which is what the row-strike visual
// (every variant) keys on. It's also Cutthroat's own scoring gate (see
// awardScoreToOpenOpponents/canScore below) - self-credit games (Cricket,
// Tactics, Random Cricket) gate scoring on the stricter isSoleCloser
// instead, which can go false well before this goes true.
export function isTargetDead(state, players, target) {
    return players.every(player => state[player][target].marks >= 3);
}

// True only while `player` is the one and only player who has closed (3+
// marks on) this target right now. Cricket/Tactics/Random Cricket's own
// scoring gate: the moment a second player also closes a number, nobody -
// not even the first closer - can score on it any more (unlike Cutthroat's
// "closing makes you immune, everyone else keeps paying" rule, where
// multiple closers coexisting is normal). Note this is about SCORING only -
// the row itself doesn't strike through until isTargetDead too (every
// player closed), even though nobody can score on it any more before then.
export function isSoleCloser(state, players, target, player) {
    if (state[player][target].marks < 3) return false;
    return players.every(other => other === player || state[other][target].marks < 3);
}

// Normal cricket's awardScore: the throwing player keeps the points.
export function awardScoreToSelf(state, players, player, target) {
    const amount = targetValue(target);
    state[player][target].score += amount;
    return [{ recipient: player, amount }];
}

// Cutthroat's awardScore: every OTHER player who hasn't closed this target
// yet takes the points (closing a number makes you immune to attacks on it).
export function awardScoreToOpenOpponents(state, players, player, target) {
    const amount = targetValue(target);
    const credits = [];
    players.forEach(other => {
        if (other === player) return;
        if (state[other][target].marks >= 3) return; // closed - immune
        state[other][target].score += amount;
        credits.push({ recipient: other, amount });
    });
    return credits;
}

// Applies one hit and returns an effect describing exactly what happened -
// {type: 'mark', player, target} or {type: 'score', target, credits} - or
// null if this player can't score on the target right now (a genuine
// no-op). `canScore` is the variant's own gate - isTargetDead-based
// ("not dead for everyone") for Cutthroat, isSoleCloser-based ("I'm the
// only one who's closed it") for Cricket/Tactics/Random Cricket.
export function applyHit(state, players, player, target, awardScore, canScore) {
    const cell = state[player][target];
    if (cell.marks < 3) {
        cell.marks += 1;
        return { type: 'mark', player, target };
    }
    if (!canScore(state, players, player, target)) {
        return null;
    }
    const credits = awardScore(state, players, player, target);
    return { type: 'score', target, credits };
}

export function undoEffect(state, effect) {
    if (!effect) return;
    if (effect.type === 'mark') {
        state[effect.player][effect.target].marks -= 1;
    } else {
        effect.credits.forEach(({ recipient, amount }) => {
            state[recipient][effect.target].score -= amount;
        });
    }
}

// Records the effect onto `history` only when the hit actually changed
// something - a hit that can't score right now leaves nothing to undo.
export function addHit(state, players, player, target, history, awardScore, canScore) {
    const effect = applyHit(state, players, player, target, awardScore, canScore);
    if (effect) history.push(effect);
}

// Undoes whatever the most recent addHit did, wherever on the board (and
// whoever it credited) it happened. Returns the undone effect, or null if
// there was nothing to undo.
export function undoLast(state, history) {
    const last = history.pop();
    if (!last) return null;
    undoEffect(state, last);
    return last;
}

export function totalScore(state, player, targets) {
    return targets.reduce((sum, target) => sum + state[player][target].score, 0);
}

// A player wins the instant they've closed every target and their score is
// as good as everyone else's - "as good" means highest normally, lowest in
// Cutthroat. The game can end mid-round.
export function isGameComplete(state, players, targets, lowestWins) {
    return players.some(player => {
        const closedAll = targets.every(target => state[player][target].marks >= 3);
        if (!closedAll) return false;
        const myScore = totalScore(state, player, targets);
        return players.every(other => {
            const otherScore = totalScore(state, other, targets);
            return lowestWins ? myScore <= otherScore : myScore >= otherScore;
        });
    });
}

export function cellContent(state, players, player, target, canScore) {
    const { marks } = state[player][target];
    if (marks === 0) {
        return { html: '<span class="marks-empty">··</span>', classes: [] };
    }
    if (marks === 1) {
        return { html: markIcon('marks-1'), classes: ['filled'] };
    }
    if (marks === 2) {
        return { html: markIcon('marks-2'), classes: ['filled'] };
    }
    const dead = !canScore(state, players, player, target);
    return { html: markIcon(dead ? 'dead' : 'scorable'), classes: ['filled', dead ? 'dead' : 'scorable'] };
}

// Inline SVG (not <img>) so each mark state's color can be driven by the
// active theme's CSS custom properties via currentColor, rather than being
// baked into a raster file per theme.
function markIcon(stateClass) {
    if (stateClass === 'marks-1') {
        return `<svg class="marks-icon ${stateClass}" viewBox="0 0 24 24">
            <line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
        </svg>`;
    }
    if (stateClass === 'marks-2') {
        return `<svg class="marks-icon ${stateClass}" viewBox="0 0 24 24">
            <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
        </svg>`;
    }
    // 3 marks - a circled X, colored differently depending on whether it's
    // dead (closed for everyone) or still scorable (closed for this player,
    // open for at least one other).
    return `<svg class="marks-icon ${stateClass}" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2.5"/>
        <line x1="7.5" y1="7.5" x2="16.5" y2="16.5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
        <line x1="16.5" y1="7.5" x2="7.5" y2="16.5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    </svg>`;
}

// Builds a full game definition for one closing-game variant. `getTargets`
// is called once per new game session (not once at module load), so a
// variant can either return the same fixed list every time (Cricket:
// 15-20 + bull; Tactics: 10-20 + bull) or generate a fresh one per game
// (Random Cricket: 6 random numbers + bull) - the rest of the engine and the
// controller that drives it don't need to know which. `awardScore` and
// `lowestWins` default to normal cricket's rules (self-credit, highest
// score wins); Cutthroat overrides both.
//
// `soleCloserScoring` (default false, Cutthroat's own setting) picks the
// scoring gate: false uses isTargetDead - a closed number stays scorable by
// anyone who's closed it until every player has (Cutthroat's "closing makes
// you immune" model needs several closers to coexist). true uses
// isSoleCloser instead - Cricket/Tactics/Random Cricket's rule, where a
// second player closing a number shuts scoring off for everyone, including
// whoever closed it first. Either way, the row-strike visual (isRowDead)
// stays keyed on isTargetDead - it marks "every player has closed this
// target, marks are done changing", not "nobody can score any more", which
// can genuinely happen earlier under soleCloserScoring without the row
// itself being finished.
export function createClosingGame({
    id, name, description, getTargets, minPlayers = 2,
    awardScore = awardScoreToSelf, lowestWins = false, soleCloserScoring = false
}) {
    const canScore = soleCloserScoring
        ? (state, players, player, target) => isSoleCloser(state, players, target, player)
        : (state, players, player, target) => !isTargetDead(state, players, target);

    return {
        id,
        name,
        description,
        minPlayers,
        getTargets,
        buildRounds: (targets) => targets.map(target => ({ name: String(target), description: '' })),
        createState: (players, targets) => createClosingGameState(players, targets),
        createHistory: () => [],
        addHit: (state, players, player, target, history) => addHit(state, players, player, target, history, awardScore, canScore),
        undoLast: (state, history) => undoLast(state, history),
        cellContent: (state, players, player, target) => cellContent(state, players, player, target, canScore),
        isRowDead: (state, players, target) => isTargetDead(state, players, target),
        isComplete: (state, players, targets) => isGameComplete(state, players, targets, lowestWins),
        totalScore: (state, player, targets) => totalScore(state, player, targets),
        rankPlayers: (players, scores) => players.slice().sort((a, b) => lowestWins ? scores[a] - scores[b] : scores[b] - scores[a])
    };
}
